<script setup lang="ts">
import { computed, toRefs } from 'vue';
import useLocale from '../../composables/useLocale';
import { useDisplay } from 'vuetify';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faMessageCaptions } from '@fortawesome/pro-regular-svg-icons';
import { faVolumeHigh } from '@fortawesome/pro-solid-svg-icons';
import { storeToRefs } from 'pinia';
import useReaderStore from '../../stores/reader';

library.add([faMessageCaptions, faVolumeHigh]);

type LanguageItem = {
  title: string;
  value: string;
};

type Props = {
  titleKey?: string;
  icon?: string;
  items: LanguageItem[];
  compare?: string;
  onSelect: (languageCode: string) => void;
};
const props = defineProps<Props>();

const { compare, items } = toRefs(props);

const { t } = useLocale();
const { smAndDown } = useDisplay();
const { isAudioOnly } = storeToRefs(useReaderStore());

const selectedItem = computed(() => items.value.find((item) => item.value === compare.value));
const unselectedItems = computed(() => items.value.filter((item) => item.value !== compare.value));
</script>

<template>
  <v-card-text
    class="flex-grow-1 w-50 pa-0"
    :class="{
      'width-screen-50': smAndDown,
      'min-width-240': !smAndDown,
      'pa-5': isAudioOnly,
      'w-100': isAudioOnly && !smAndDown,
    }"
  >
    <v-list bg-color="transparent" mandatory class="overflow-y-auto" :selected="[compare]">
      <v-list-subheader v-if="!isAudioOnly && icon && titleKey" class="text-surface">
        <FontAwesomeIcon class="mr-2" :icon="icon" size="lg"></FontAwesomeIcon>
        <span>{{ t(titleKey) }}</span>
      </v-list-subheader>

      <v-list-item
        v-if="selectedItem"
        :value="selectedItem.value"
        :title="selectedItem.title"
        :data-testid="`bp-language-item-${selectedItem.value}`"
        @click.stop="onSelect(selectedItem.value)"
        :aria-label="t('book_player.label_language_is_selected', { language: selectedItem.title })"
      >
        <template #append>
          <FontAwesomeIcon icon="fa-solid fa-check" size="sm"></FontAwesomeIcon>
        </template>
      </v-list-item>

      <v-divider v-if="selectedItem && unselectedItems.length" class="my-1" />

      <v-list-item
        v-for="(item, index) in unselectedItems"
        :key="index"
        :value="item.value"
        :title="item.title"
        :data-testid="`bp-language-item-${item.value}`"
        @click.stop="onSelect(item.value)"
      />
    </v-list>
  </v-card-text>
</template>

<style scoped lang="scss">
.min-with-240 {
  min-width: 240px !important;
}
.width-screen-50 {
  width: 50vw !important;
}
</style>
